import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";
import { fetchAllWeaknesses } from "../../redux/actions/weaknessesActionCreators";
import { Autocomplete, useMediaQuery } from "@mui/material";
import {
  requestAddUseCase,
  requestEditUseCase,
} from "../../redux/actions/useCaseActionCreators";
import { useHistory } from "react-router-dom";
import DialogContentText from "@mui/material/DialogContentText";
import { translate } from "../../utils/utils";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import AutocompleteWithPagination from "../../components/AutocompleteWithPagination";

export default function AddModal({
  open,
  handleClose,
  initialValues,
  isUpdate,
  usecaseId,
  title,
  difficulty,
  accountId,
  accountType,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const allWeaknesses = useSelector(
    (state) => state.weaknesses.infiniteWeaknesses
  );
  const totalPages = useSelector((state) => state.weaknesses.totalPages);

  const [difficultyLevel, setDifficultyLevel] = useState("");
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedWeakness, setSelectedWeakness] = useState({});
  const [error, setError] = useState(false);
  const [input, setInput] = useState("");
  const handleSelectedWeakness = (obj) => {
    setSelectedWeakness(obj);
  };

  useEffect(() => {
    if (Object.keys(selectedWeakness).length > 0) {
      setInput(selectedWeakness[translate("title")]);
    } else {
      setInput("");
    }
  }, [selectedWeakness]);

  const handleError = (bol) => {
    setError(bol);
  };

  useEffect(() => {
    setSearchText(input);
  }, [input]);

  useEffect(() => {
    dispatch(fetchAllWeaknesses(page, searchText, "all", false, true));
  }, [dispatch, searchText, page]);

  useEffect(() => {
    if (difficulty) {
      setDifficultyLevel(difficulty);
    }
  }, [difficulty]);

  const handleInputChange = (value) => {
    setInput(value);
  };

  const isMobile = useMediaQuery("(max-width:900px)");

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop === clientHeight && totalPages > page) {
      setPage(page + 1);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      title: Yup.string().required(t("required_field")),
      description: Yup.string().required(t("required_field")),
      threshold: Yup.number()
        .required(t("required_field"))
        .nullable()
        .min(1, `${t("number_control")} 100`)
        .max(100, `${t("number_control")} 100`),
      uc_coefficient: Yup.number()
        .required(t("required_field"))
        .nullable()
        .min(1)
        .max(10),
    }),
    onSubmit: (values, { resetForm }) => {
      if (Object.keys(selectedWeakness).length === 0) {
        setError(true);
      } else {
        const { related_weakness, ...rest } = values;
        if (isUpdate) {
          dispatch(
            requestEditUseCase(
              {
                ...rest,
                idw: selectedWeakness?.id,
                difficulty_level: difficultyLevel,
              },
              usecaseId,
              accountId,
              accountType
            )
          );
        } else {
          dispatch(
            requestAddUseCase(
              {
                ...rest,
                idw: selectedWeakness?.id,
                difficulty_level: difficultyLevel,
              },
              history,
              accountId,
              accountType
            )
          );
        }
        handleClose();
      }
    },
  });
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        PaperProps={{ sx: { padding: "20px " } }}
        disableBackdropClick
        disableEnforceFocus
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <IconButton onClick={handleClose}>
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            <Typography variant="title4">{title}</Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText mb={3}>
              {t("add_usecase_description")}
            </DialogContentText>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  {t("name")}
                </Typography>
                <TextField
                  error={Boolean(formik.touched.title && formik.errors.title)}
                  fullWidth
                  helperText={formik.touched.title && formik.errors.title}
                  name={"title"}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  Description
                </Typography>
                <TextField
                  error={Boolean(
                    formik.touched.description && formik.errors.description
                  )}
                  fullWidth
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  name="description"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  {t("threshold")}
                </Typography>
                <TextField
                  type="Number"
                  error={Boolean(
                    formik.touched.threshold && formik.errors.threshold
                  )}
                  fullWidth
                  helperText={
                    formik.touched.threshold && formik.errors.threshold
                  }
                  name="threshold"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.threshold}
                  InputProps={{
                    inputProps: { min: 1, max: 100 },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  Coefficient
                </Typography>
                <TextField
                  type="Number"
                  error={Boolean(
                    formik.touched.uc_coefficient &&
                      formik.errors.uc_coefficient
                  )}
                  fullWidth
                  helperText={
                    formik.touched.uc_coefficient &&
                    formik.errors.uc_coefficient
                  }
                  name="uc_coefficient"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.uc_coefficient}
                  InputProps={{
                    inputProps: { min: 0, max: 10 },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  {t("level")}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    displayEmpty
                    style={{ height: "auto" }}
                    value={difficultyLevel}
                    onChange={(e) => setDifficultyLevel(e.target.value)}
                  >
                    <MenuItem value="Easy">{t("Easy")}</MenuItem>
                    <MenuItem value="Medium">{t("Medium")}</MenuItem>
                    <MenuItem value="Hard">{t("Hard")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ padding: "5px 0px" }}>
                  {t("related_weakness")}
                </Typography>
                {/* <Autocomplete
                  name="idw"
                  getOptionLabel={(option) => option[translate("title")]}
                  value={formik.values.related_weakness}
                  disablePortal
                  onInputChange={handleInputChange}
                  loading={loading}
                  id="combo-box-demo"
                  options={allWeaknesses}
                  onChange={(e, value) =>
                    formik.setFieldValue("related_weakness", value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={
                        formik.touched.related_weakness
                          ? formik.errors.related_weakness
                          : ""
                      }
                      error={
                        formik.touched.related_weakness &&
                        Boolean(formik.errors.related_weakness)
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                /> */}
                <AutocompleteWithPagination
                  options={allWeaknesses}
                  isMobile={isMobile}
                  handleSearchText={handleInputChange}
                  selected={selectedWeakness}
                  handleSelected={handleSelectedWeakness}
                  value={input}
                  error={error}
                  handleError={handleError}
                  handleScroll={handleScroll}
                  width={isMobile ? "75%" : "90%"}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ padding: "20px" }}>
            <Grid container>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="button"
                  type="submit"
                  fullWidth
                >
                  {t("save")}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
